import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiThermometer, 
  FiHome, FiAlertTriangle, FiLayers, FiDollarSign, 
  FiSun, FiCheck, FiX, FiInfo, FiTool, FiClock 
} = FiIcons;

function RoofInsulation() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Dachdämmung richtig planen: So sparst du massiv Heizkosten und vermeidest teure Fehler",
    "alternativeHeadline": "Oberste Geschossdecke vs. Dachschräge: Der große Vergleich",
    "image": "https://images.unsplash.com/photo-1632759145351-1d592919f522?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    "author": {
      "@type": "Person",
      "name": "Maxim Kelm"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-AUSBAU",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-04-02",
    "description": "15–20 % Heizkosten sparen durch richtige Dachdämmung. Erfahre, warum 30% der Eigentümer falsch dämmen und wie du es richtig machst."
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Dachdämmung planen 2026: Fehler vermeiden & Kosten sparen"
        description="Dach dämmen oder Geschossdecke? 🏠 Anleitung für Steildächer, Einblasdämmung & Hitzeschutz. Vermeide diesen teuren 30%-Fehler!"
        keywords="Dachdämmung, Oberste Geschossdecke dämmen, Einblasdämmung Dach, Hitzeschutz Dachgeschoss, Holzbalkendecke dämmen, Dachsanierung Kosten"
        schema={articleSchema}
        canonical="/blog/dachdaemmung-richtig-planen"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link to="/blog" className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group">
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiThermometer} />
              <span>Energie & Dämmung</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Dachdämmung richtig planen: So sparst du massiv Heizkosten und vermeidest teure Fehler
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Stand: April 2026</span>
            </div>
          </header>

          {/* Hero Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl aspect-video relative">
            <img 
              src="https://images.unsplash.com/photo-1632759145351-1d592919f522?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80" 
              alt="Dachdämmung Baustelle mit Mineralwolle und Dampfbremse"
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Wärme steigt nach oben – und genau dort, im Dachbereich, verlieren viele Häuser unnötig viel Energie. Etwa <strong>15–20 % der gesamten Heizenergie</strong> gehen über das Dach verloren. Gleichzeitig kann eine gut geplante Dachdämmung bis zu 100 Jahre halten.
          </div>

          <div className="bg-mk-teal-50 p-6 rounded-xl border border-mk-teal-100 mb-12">
            <h3 className="text-mk-teal-900 font-bold mt-0 mb-4 flex items-center">
              <SafeIcon icon={FiInfo} className="mr-2"/> Was du in diesem Artikel lernst
            </h3>
            <ul className="mt-2 mb-0 space-y-2 text-sm list-none pl-0 text-gray-700">
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> <strong>Wo</strong> du dämmen solltest (Dach vs. Decke).</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> Welcher <strong>Aufbau</strong> für deinen Dachtyp (Beton vs. Holz) sinnvoll ist.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> Welchen <strong>Fehler 30 % der Eigentümer machen</strong>.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1 text-mk-teal-600"/> Wann eine große Dachsanierung wirtschaftlich ist.</li>
            </ul>
          </div>

          {/* 1. Dach ist nicht gleich Dach */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            1. Dach ist nicht gleich Dach: Die entscheidende Frage
          </h2>
          <p className="text-gray-700 mb-6">
            Bevor du auch nur einen Euro ausgibst, musst du die Nutzung deines Dachs klären. Beim klassischen Steildach gibt es zwei Szenarien:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white border-t-4 border-mk-teal-500 p-6 shadow-sm rounded-xl">
              <h4 className="font-bold text-gray-900 mb-2">Szenario A: Der kalte Spitzboden</h4>
              <p className="text-sm text-gray-600">
                Der Raum unter dem Dach ist <strong>nicht ausgebaut, nicht beheizt und nicht bewohnt</strong>. Er dient höchstens als Lagerfläche.
              </p>
              <div className="mt-4 text-mk-teal-700 font-bold text-sm bg-mk-teal-50 p-2 rounded inline-block">
                → Dämmebene: Oberste Geschossdecke
              </div>
            </div>
            <div className="bg-white border-t-4 border-mk-red-500 p-6 shadow-sm rounded-xl">
              <h4 className="font-bold text-gray-900 mb-2">Szenario B: Wohnraum bis in die Spitze</h4>
              <p className="text-sm text-gray-600">
                Der Dachraum ist ausgebaut und beheizt. Es gibt keine "oberste Decke" mehr, die Schräge ist die Außenwand.
              </p>
              <div className="mt-4 text-mk-red-700 font-bold text-sm bg-mk-red-50 p-2 rounded inline-block">
                → Dämmebene: Dachfläche (Sparren)
              </div>
            </div>
          </div>

          {/* 2. Der größte Fehler */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="mr-3 text-mk-red-600" />
            2. Der 30%-Fehler: Falsch dämmen im Spitzboden
          </h2>
          
          <div className="bg-mk-red-50 border-l-4 border-mk-red-600 p-6 rounded-r-xl mb-8">
            <p className="text-mk-red-900 font-bold m-0">
              In etwa 30 % der Spitzböden ist Dämmstoff in den Dachschrägen verbaut – obwohl der Spitzboden gar nicht bewohnt oder beheizt ist. Das ist bauphysikalisch und wirtschaftlich Unsinn!
            </p>
          </div>

          <p className="text-gray-700 mb-6">
            Warum ist das ein Problem? Ganz einfach: Die <strong>Dachfläche</strong> (Schrägen + Giebel) ist etwa <strong>doppelt so groß</strong> wie die Fläche der Geschossdecke.
          </p>

          <ul className="space-y-4 mb-8">
            <li className="flex items-start">
              <div className="bg-mk-teal-100 p-2 rounded-full mr-4 mt-1">
                <SafeIcon icon={FiDollarSign} className="text-mk-teal-700 w-4 h-4" />
              </div>
              <div>
                <strong>Kostenexplosion:</strong> Doppelte Fläche bedeutet mehr Material. Um den gleichen Dämmwert zu erreichen,brauchst du bei der Dachfläche oft die <strong>vierfache Menge</strong> an Dämmstoff im Vergleich zur Decke.
              </div>
            </li>
            <li className="flex items-start">
              <div className="bg-mk-teal-100 p-2 rounded-full mr-4 mt-1">
                <SafeIcon icon={FiLayers} className="text-mk-teal-700 w-4 h-4" />
              </div>
              <div>
                <strong>Effizienzverlust:</strong> Du heizt das Volumen des Spitzbodens indirekt mit, obwohl du ihn nicht nutzt.
              </div>
            </li>
          </ul>

          <p className="text-gray-900 font-bold border-b-2 border-mk-teal-500 inline-block mb-12">
            Merke: Unbeheizter Spitzboden? → Immer zuerst die oberste Geschossdecke dämmen!
          </p>

          {/* 3. Wie dämme ich richtig? */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            3. Wie dämme ich die oberste Geschossdecke richtig?
          </h2>
          <p className="text-gray-700 mb-6">
            Hier unterscheiden wir zwei Konstruktionstypen, die in Deutschland typisch sind.
          </p>

          <div className="space-y-8 mb-12">
            {/* Betondecke */}
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3 text-sm">A</span>
                Die Betondecke (ca. 25 %)
              </h3>
              <p className="text-gray-600 mb-4">
                Du stehst auf massivem Beton. Die Lösung ist einfach und effektiv.
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> <strong>Variante 1:</strong> Matten/Platten fugenfrei auflegen (bis 40 cm).</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-2"/> <strong>Variante 2:</strong> Einblasdämmung (Zellulose/Glasfaser) offen aufblasen.</li>
              </ul>
              <p className="text-sm text-gray-500 italic">
                Tipp: Eine Dämmdicke von 40 cm gilt heute als wirtschaftliches Optimum über 50 Jahre gesehen. Die Materialkosten steigen, aber der Arbeitsaufwand bleibt fast gleich.
              </p>
            </div>

            {/* Holzbalkendecke */}
            <div className="bg-mk-teal-50 p-8 rounded-2xl border border-mk-teal-100">
              <h3 className="text-xl font-bold text-mk-teal-900 mb-4 flex items-center">
                <span className="w-8 h-8 bg-mk-teal-200 rounded-full flex items-center justify-center mr-3 text-sm text-mk-teal-800">B</span>
                Die Holzbalkendecke (ca. 75 %)
              </h3>
              <p className="text-mk-teal-800 mb-4">
                Du stehst auf Holzdielen. Darunter ist ein Hohlraum, in dem es oft zieht ("Pfeifendeckel-Effekt").
              </p>
              <div className="bg-white p-4 rounded-xl mb-4 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-2">Das Problem:</h4>
                <p className="text-sm text-gray-600 m-0">
                  Wenn du Dämmung einfach <em>auf</em> die Dielen legst, pfeift der Wind darunter durch. Die Dämmung ist wirkungslos.
                </p>
              </div>
              <h4 className="font-bold text-mk-teal-900 mb-2">Die Lösung: Einblasdämmung</h4>
              <ul className="space-y-2 mb-0 text-mk-teal-800">
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-600 mr-2"/> Hohlräume werden mit Faserdämmstoff ausgeblasen.</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-600 mr-2"/> Stoppt den Luftzug sofort.</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-mk-teal-600 mr-2"/> Kein Abriss der Dielen nötig (sauber & schnell).</li>
              </ul>
            </div>
          </div>

          {/* 4. Dachschrägen */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            4. Dachschrägen dämmen – Spezialfall
          </h2>
          <p className="text-gray-700 mb-6">
            Nur sinnvoll, wenn der Raum bewohnt ist oder ausgebaut werden soll.
          </p>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-lg mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Der "Thermobag"-Trick (Dämmsack)</h3>
            <p className="text-gray-600 mb-4">
              Bei alten Dächern ohne Unterspannbahn darf Dämmung nicht an die Ziegel stoßen (Feuchtigkeit!). Die Lösung ist das <strong>Dämmsackverfahren</strong>:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Ein großer Sack wird in das Gefach geschoben.</li>
              <li>Der Sack wird fixiert und aufgeblasen.</li>
              <li>Dämmstoff wird in den Sack eingeblasen.</li>
            </ol>
            <p className="mt-4 text-sm font-bold text-mk-teal-600">
              Ergebnis: Fugenfreie Dämmung, geschützt vor Feuchtigkeit, ohne das Dach abzudecken.
            </p>
          </div>

          {/* 5. Kostenvergleich */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiDollarSign} className="mr-3 text-mk-teal-600" />
            5. Was kostet das? (Preisschätzung 2026)
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="text-center p-6 bg-green-50 rounded-2xl border border-green-100">
              <h4 className="text-green-800 font-bold mb-2">Die smarte Lösung</h4>
              <p className="text-sm text-green-700 mb-4">Einblasdämmung Geschossdecke + Dachschrägen</p>
              <p className="text-3xl font-bold text-green-900">ca. 5.000 – 7.000 €</p>
              <div className="mt-2 text-xs text-green-600">Amortisation oft &lt; 5 Jahre</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <h4 className="text-gray-800 font-bold mb-2">Die große Lösung</h4>
              <p className="text-sm text-gray-600 mb-4">Komplett neues Dach (Eindeckung + Dämmung)</p>
              <p className="text-3xl font-bold text-gray-900">50.000 – 100.000 €</p>
              <div className="mt-2 text-xs text-gray-500">Lohnt sich nur bei kaputtem Dach</div>
            </div>
          </div>

          {/* 6. Materialwahl */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            6. Material: Warum "hart" hier schlecht ist
          </h2>
          <p className="text-gray-700 mb-6">
            Viele Heimwerker greifen zu starren Platten (Styropor/PUR), um sie zwischen die Sparren zu klemmen. <strong>Tu das nicht!</strong>
          </p>
          <div className="flex flex-col md:flex-row gap-4 mb-12">
            <div className="flex-1 bg-red-50 p-4 rounded-lg flex items-start">
              <SafeIcon icon={FiX} className="text-red-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <strong className="block text-red-900">Starre Platten</strong>
                <span className="text-sm text-red-800">Lassen sich nie fugenfrei an krumme Sparren anpassen. Es entstehen Wärmebrücken.</span>
              </div>
            </div>
            <div className="flex-1 bg-green-50 p-4 rounded-lg flex items-start">
              <SafeIcon icon={FiCheck} className="text-green-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <strong className="block text-green-900">Flexible Matten / Einblasdämmung</strong>
                <span className="text-sm text-green-800">Schmiegen sich an jeden Balken an. Fugenfrei = Perfekte Dämmwirkung.</span>
              </div>
            </div>
          </div>

          {/* 7. Hitzeschutz */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiSun} className="mr-3 text-mk-teal-600" />
            7. Mythos Hitzeschutz
          </h2>
          <p className="text-gray-700 mb-4">
            "Ich nehme Holzfaser, damit es im Sommer kühl bleibt." – Das ist physikalisch korrekt (Phasenverschiebung), aber bei einer guten Dämmstärke (ab 20-30 cm) spielt das Material nur noch eine untergeordnete Rolle.
          </p>
          <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl mb-12">
            <p className="text-orange-900 font-bold m-0">
              Der wahre Gamechanger für kühle Räume: <br/>
              <span className="uppercase text-lg">Außenliegende Verschattung!</span>
            </p>
            <p className="text-orange-800 text-sm mt-2">
              Sobald die Sonne durch das Fensterglas kommt, ist die Hitze drin. Rollos oder Markisen <strong>vor</strong> dem Fenster bringen mehr als der teuerste Dämmstoff.
            </p>
          </div>

          {/* Fazit */}
          <div className="bg-mk-teal-600 text-white p-10 rounded-[2.5rem] shadow-2xl mt-16 mb-12">
            <h2 className="text-white text-3xl font-bold mb-6 mt-0">Fazit: Schritt für Schritt zur Dämmung</h2>
            <ol className="space-y-4 mb-8 list-decimal list-inside text-mk-teal-50 font-medium">
              <li><strong>Analysiere die Nutzung:</strong> Unbeheizter Spitzboden? Dann dämme die Decke, nicht das Dach!</li>
              <li><strong>Prüfe den Bestand:</strong> Holzbalkendecke? Stoppe den Luftzug mit Einblasdämmung.</li>
              <li><strong>Wähle weiches Material:</strong> Flexible Matten oder Einblasdämmung statt harter Platten.</li>
              <li><strong>Denk an den Sommer:</strong> Plane Verschattung für alle Dachfenster ein.</li>
            </ol>
            <p className="text-white/90 text-sm italic border-t border-mk-teal-400 pt-6">
              Wer diese Punkte beachtet, holt energetisch das Maximum aus seinem Haus heraus – ohne ein Vermögen für unnötige Maßnahmen zu verschwenden.
            </p>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ist Ihr Dach fit für die Zukunft?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir prüfen Ihre Situation vor Ort in Heinsberg: Lohnt sich eine Einblasdämmung? Ist das Dach dicht? Wir erstellen Ihnen einen ehrlichen Sanierungsplan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Kostenlosen Dämm-Check anfragen
              </Link>
              <Link 
                to="/daemmtechnik" 
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zu unseren Dämm-Lösungen
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default RoofInsulation;